import { Button, Input, Modal, Textarea } from '@orobix/orobix-fe-components'
import { useMutation } from '@tanstack/react-query'
import { Formik, FormikErrors } from 'formik'
import { useTranslation } from 'react-i18next'

import { ErrorText } from '../../components/ErrorText/ErrorText'
import { Training } from '../../store/store.types'
import { update } from '../../utils/api'

type FormValues = {
  name: string
  notes: string
}

type TrainingsUpdateProps = {
  onClose: () => void
  onSuccess: () => void
  trainingId: number
  initialValues?: FormValues
}

const TrainingsUpdate = ({
  onClose,
  onSuccess,
  trainingId,
  initialValues = {
    name: '',
    notes: '',
  },
}: TrainingsUpdateProps) => {
  const { t } = useTranslation()

  const { mutate, isLoading, isError } = useMutation(
    (values: FormValues) =>
      update<Training>(`/trainings/${trainingId}/`, values),
    { onSuccess, useErrorBoundary: false }
  )

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        if (!values.name) {
          errors.name = t('This field is required')
        }

        return errors
      }}
      onSubmit={(values, { setSubmitting }) => {
        mutate(values)
        setSubmitting(false)
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Modal
            size="md"
            onCloseRequest={onClose}
            title={t('Training details')}
            footer={
              <Button
                label={t('Edit')}
                type="action"
                disabled={isSubmitting}
                loading={isLoading}
              />
            }
          >
            <Input
              name="name"
              id="name"
              label={t('Name')}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('name', e.target.value)
              }
              onBlur={handleBlur}
              value={values.name}
              error={!!(errors.name && touched.name)}
              errorMessage={errors.name}
              disabled={isSubmitting}
            />

            <Textarea
              name="notes"
              id="notes"
              label={t('Notes')}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFieldValue('notes', e.target.value)
              }
              onBlur={handleBlur}
              value={values.notes}
              error={!!(errors.notes && touched.notes)}
              errorMessage={errors.notes}
              disabled={isSubmitting}
            />

            {isError && <ErrorText />}
          </Modal>
        </form>
      )}
    </Formik>
  )
}

export default TrainingsUpdate
